import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { CustomFetchError } from "@/client/custom-fetch";
import { useToast } from "@/components";
import { config } from "@/config/config";
import { hasPartnerPortalAccess, useAuth } from "@/context/auth";
import { useI18n } from "@/context/i18n";
import { usePermissions } from "@/context/permissions";

import { loginApi } from "./login.api";
import { ILoginHooks } from "./login.hooks.interface";
import {
  forgotPasswordSchema,
  loginSchema,
  twoFactorSchema,
} from "./login.schema";
import {
  IFacebookLoginRequest,
  IForgotPasswordFormValues,
  IForgotPasswordRequest,
  IGoogleLoginRequest,
  ILoginFormValues,
  ILoginRequest,
  ITwoFactorFormValues,
  ITwoFactorRequest,
} from "./login.types";

export const useLogin = (): ILoginHooks => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { handleAuthenticate } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useI18n();
  const { isLoading: permissionsLoading, hasPermission } = usePermissions();
  const facebookAppId = config.facebookAuthAppId;
  const googleRecaptchaSiteKey = config.googleReCaptchaSiteKey;
  const reCaptchaRef = React.useRef<ReCAPTCHA>(null);
  const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] =
    React.useState<boolean>(false);

  const redirectUrl = React.useMemo(() => {
    let destination = "/dashboard";
    if (searchParams.get("redirectUrl"))
      destination = searchParams.get("redirectUrl") as string;
    if (location.state?.from) destination = location.state.from;
    return destination;
  }, [searchParams, location.state?.from]);

  const handleRedirect = React.useCallback(
    (loginRoles?: { name: string }[], organizationRootId?: string) => {
    // Aguardar um pouco para que as permissões sejam carregadas
    const checkPermissionsAndRedirect = () => {
      if (permissionsLoading) {
        // Se ainda está carregando, tentar novamente após um delay
        setTimeout(checkPermissionsAndRedirect, 100);
        return;
      }

      // Se tem uma URL de redirecionamento específica (externa), usar ela
      if (redirectUrl.startsWith("http") || redirectUrl.startsWith("https")) {
        return (window.location.href = redirectUrl);
      }

      if (
        hasPartnerPortalAccess(loginRoles) &&
        organizationRootId
      ) {
        return navigate(`/clients/${organizationRootId}`, { replace: true });
      }

      // IMPORTANTE: Priorizar dashboard se o usuário tiver permissão
      // IGNORAR redirectUrl se tiver permissão de dashboard
      const hasDashboardPermission = hasPermission("dashboard:view");

      // Se tem permissão de dashboard, SEMPRE redirecionar para /dashboard
      // Ignorar qualquer redirectUrl que possa estar definido
      if (hasDashboardPermission) {
        return navigate("/dashboard", { replace: true });
      }

      // Se não tem permissão de dashboard, NÃO usar redirectUrl que pode apontar para dashboard/reports
      // Em vez disso, deixar o AppRoutes decidir para onde redirecionar baseado nas permissões
      return navigate("/dashboard", { replace: true });
    };

    // Iniciar verificação após um pequeno delay para dar tempo das permissões começarem a carregar
    setTimeout(checkPermissionsAndRedirect, 200);
  },
  [redirectUrl, navigate, permissionsLoading, hasPermission]
  );

  const invalidateAuthSensitiveCaches = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["users"] });
    void queryClient.invalidateQueries({ queryKey: ["products"] });
    void queryClient.invalidateQueries({ queryKey: ["all-products"] });
  }, [queryClient]);

  const form = useForm<ILoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const forgotPasswordForm = useForm<IForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const twoFactorForm = useForm<ITwoFactorFormValues>({
    resolver: zodResolver(twoFactorSchema),
    defaultValues: {
      passcode: "",
      userId: "",
      email: "",
      method: "APP",
    },
  });
  const twoFactorRequired = twoFactorForm.watch("requirePasscode");

  const loginMutation = useMutation({
    mutationFn: (data: ILoginRequest) => loginApi.login(data),
    onSuccess: (data) => {
      if (typeof data === "object" && "requirePasscode" in data) {
        twoFactorForm.reset(data);
        return;
      }
      handleAuthenticate(
        {
          id: data.id,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          token: data.token,
          organization_root_id: data.organization_root_id,
          roles: data.roles,
        },
        "api"
      );
      invalidateAuthSensitiveCaches();
      toast({
        title: t("auth.loginSuccess"),
        description: t("generic.redirecting"),
      });
      handleRedirect(data.roles, data.organization_root_id);
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message || t("auth.loginFailed"),
        variant: "destructive",
      });
    },
  });

  const loginWithGoogleMutation = useMutation({
    mutationFn: (data: IGoogleLoginRequest) => loginApi.loginWithGoogle(data),
    onSuccess: (data) => {
      if (typeof data === "object" && "requirePasscode" in data) {
        twoFactorForm.reset(data);
        return;
      }
      handleAuthenticate(
        {
          id: data.id,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          token: data.token,
          organization_root_id: data.organization_root_id,
          roles: data.roles,
        },
        "google"
      );
      invalidateAuthSensitiveCaches();
      toast({
        title: t("auth.loginSuccess"),
        description: t("generic.redirecting"),
      });
      handleRedirect(data.roles, data.organization_root_id);
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message || t("auth.loginFailed"),
        variant: "destructive",
      });
    },
  });

  const loginWithFacebookMutation = useMutation({
    mutationFn: (data: IFacebookLoginRequest) =>
      loginApi.loginWithFacebook(data),
    onSuccess: (data) => {
      if (typeof data === "object" && "requirePasscode" in data) {
        twoFactorForm.reset(data);
        return;
      }
      handleAuthenticate(
        {
          id: data.id,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          token: data.token,
          organization_root_id: data.organization_root_id,
          roles: data.roles,
        },
        "facebook"
      );
      invalidateAuthSensitiveCaches();
      toast({
        title: t("auth.loginSuccess"),
        description: t("generic.redirecting"),
      });
      handleRedirect(data.roles, data.organization_root_id);
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message || t("auth.loginFailed"),
        variant: "destructive",
      });
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: (data: IForgotPasswordRequest) => loginApi.forgotPassword(data),
    onSuccess: () => {
      setForgotPasswordDialogOpen(false);
      toast({
        title: t("auth.emailSent"),
        description: t("auth.checkEmailToResetPassword"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("auth.emailSentFailed"),
        description: error.message || t("auth.emailSentFailedMessage"),
        variant: "destructive",
      });
    },
  });

  const twoFactorMutation = useMutation({
    mutationFn: (data: ITwoFactorRequest) => loginApi.twoFactor(data),
    onSuccess: (data) => {
      handleAuthenticate(
        {
          id: data.id,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          token: data.token,
          organization_root_id: data.organization_root_id,
          roles: data.roles,
        },
        "api"
      );
      invalidateAuthSensitiveCaches();
      handleRedirect(data.roles, data.organization_root_id);
    },
    onError: (error: CustomFetchError) => {
      const status = error.status;
      twoFactorForm.setValue("passcode", "");
      twoFactorForm.setError("passcode", {
        message: error.message,
      });
      if (status === 410) {
        form.reset();
        twoFactorForm.clearErrors();
        twoFactorForm.reset({
          email: "",
          userId: "",
          method: undefined,
          requirePasscode: false,
        });
      }
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onCancelTwoFactor = React.useCallback(() => {
    twoFactorForm.reset({
      passcode: "",
      userId: "",
      email: "",
      method: "APP",
      requirePasscode: false,
    });
  }, [twoFactorForm]);

  const handleLogin = React.useCallback(
    async (data: ILoginRequest) => {
      try {
        // const reCaptchaToken = await reCaptchaRef?.current?.executeAsync();
        // if (!reCaptchaToken) {
        //   toast({
        //     title: t("generic.error"),
        //     description: t("auth.reCaptchaFailed"),
        //     variant: "destructive",
        //   });
        //   return;
        // }
        return loginMutation.mutate({
          ...data,
          // reCaptchaToken,
        });
      } catch (error) {
        console.error(error);
      } finally {
        reCaptchaRef.current?.reset();
      }
    },
    [loginMutation]
  );

  const onSuccessGoogle = React.useCallback(
    async (data: IGoogleLoginRequest) => {
      return loginWithGoogleMutation.mutate(data);
    },
    [loginWithGoogleMutation]
  );

  const onSuccessFacebook = React.useCallback(
    async (data: IFacebookLoginRequest) => {
      return loginWithFacebookMutation.mutate(data);
    },
    [loginWithFacebookMutation]
  );

  const handleForgotPassword = React.useCallback(async () => {
    const email = forgotPasswordForm.getValues("email");
    return forgotPasswordMutation.mutate({ email });
  }, [forgotPasswordMutation, forgotPasswordForm]);

  const onOpenForgotPasswordDialog = React.useCallback(
    (value: boolean) => {
      setForgotPasswordDialogOpen(value);
      forgotPasswordForm.reset();
    },
    [setForgotPasswordDialogOpen, forgotPasswordForm]
  );

  const onSubmitTwoFactor = React.useCallback(
    async (data: ITwoFactorFormValues) => {
      return twoFactorMutation.mutate({
        userId: data.userId,
        email: data.email,
        passcode: data.passcode,
        method: data.method,
      });
    },
    [twoFactorMutation]
  );

  return {
    handleLogin,
    onSuccessGoogle,
    onSuccessFacebook,
    facebookAppId,
    handleForgotPassword,
    form,
    forgotPasswordForm,
    googleRecaptchaSiteKey,
    reCaptchaRef,
    forgotPasswordDialogOpen,
    onOpenForgotPasswordDialog,
    twoFactorForm,
    onSubmitTwoFactor,
    onCancelTwoFactor,
    twoFactorRequired,
  };
};
